import type { TenantBrand } from '@tendedloop/shared';
import type { PublicBrand, ResolvedBrand } from '@tendedloop/shared/brand-public';
import { PUBLIC_HANDLE_RE } from '@tendedloop/shared/brand-public';

/**
 * Where the portal's brand comes from, and which one wins. Pure: no React, no
 * network; storage only through the small helpers at the end.
 *
 * Three sources, in this order of precedence:
 * 1. token: the office that sent the link in the URL (`POST /auth/brand`).
 *    Valid and expired links both answer. A link with no brand to show skips
 *    the device's session (it may be someone else's) and leaves the hint.
 * 2. session: the signed-in resident's own office (`/me/office`), kept on this
 *    device per org handle so a returning resident's sign-in shows it too.
 * 3. hint: `?o=<handle>` from a link (`GET /public/brand/:handle`). Kept for
 *    this browser session only (sessionStorage), never on the device.
 *
 * A source is absent (nothing to ask), pending (asked), failed (no answer in
 * time: the next source decides) or ready (its answer, null = TendedLoop's look).
 * While the deciding source is pending, screens show a neutral skeleton band,
 * never TendedLoop's logo that would then swap for the building's.
 */

export type Source =
  | { state: 'absent' }
  | { state: 'pending' }
  | { state: 'failed' }
  | { state: 'ready'; brand: TenantBrand | null; handle: string | null };

export const ABSENT: Source = { state: 'absent' };
export const PENDING: Source = { state: 'pending' };
export const FAILED: Source = { state: 'failed' };
export const ready = (brand: TenantBrand | null, handle: string | null = null): Source => ({
  state: 'ready',
  brand,
  handle,
});

export interface Picked {
  /** The deciding source has not answered yet: show the skeleton band. */
  loading: boolean;
  brand: TenantBrand | null;
  /** The org handle of the brand shown (for the home-screen app), when known. */
  handle: string | null;
  from: 'token' | 'session' | 'hint' | null;
}

export function pickBrand(s: { token: Source; session: Source; hint: Source }): Picked {
  // A link whose office has no brand to show (replaced, unknown, unverified) is
  // not this device's session: only the link's own hint may still name its org.
  const linkWithoutBrand = s.token.state === 'ready' && !s.token.brand;
  const order = linkWithoutBrand ? (['hint'] as const) : (['token', 'session', 'hint'] as const);
  for (const from of order) {
    const src = s[from];
    if (src.state === 'absent' || src.state === 'failed') continue;
    if (src.state === 'pending') return { loading: true, brand: null, handle: null, from };
    // A hint without a brand (unknown or unverified org) leaves TendedLoop's look.
    return { loading: false, brand: src.brand, handle: src.brand ? src.handle : null, from };
  }
  return { loading: false, brand: null, handle: null, from: null };
}

/**
 * The session source from this device's cache. Signed in, the cache is the
 * resident's own office. Signed out (the session ended), it still greets a
 * returning resident, unless a link names another org: then the link's hint
 * decides, so nobody sees someone else's building.
 */
export function sessionSource(o: {
  signedIn: boolean;
  cached: { handle: string; brand: TenantBrand } | null;
  hintHandle: string | null;
  officePending: boolean;
}): Source {
  if (o.officePending) return PENDING;
  if (!o.cached) return ABSENT;
  if (!o.signedIn && o.hintHandle && o.hintHandle !== o.cached.handle) return ABSENT;
  return ready(o.cached.brand, o.cached.handle);
}

// ---------------------------------------------------------------------------
// The brand as the portal paints it (TenantBrand's shape, nothing null)
// ---------------------------------------------------------------------------

/**
 * A server brand (PublicBrand from the signed-out lookups, ResolvedBrand from
 * /me/office) as the look the portal paints: null fields dropped. Only what a
 * screen uses is kept.
 */
export function lookOf(b: PublicBrand | ResolvedBrand | null | undefined): TenantBrand | null {
  if (!b || typeof b !== 'object') return null;
  const r = b as Partial<ResolvedBrand>;
  const out: TenantBrand = {};
  const str = (k: keyof TenantBrand & keyof ResolvedBrand) => {
    const v = r[k];
    if (typeof v === 'string' && v) (out as Record<string, unknown>)[k] = v;
  };
  for (const k of [
    'displayName',
    'logoOnDarkUrl',
    'partnerLogoOnDarkUrl',
    'emailLogoUrl',
    'splashUrl',
    'heroImageUrl',
    'appIconUrl',
    'primary',
    'done',
    'highlight',
  ] as const)
    str(k);
  if (r.logoBackground === 'light' || r.logoBackground === 'dark') out.logoBackground = r.logoBackground;
  if (r.heroIsRender) out.heroIsRender = true;
  if (Array.isArray(r.features) && r.features.length) {
    out.features = r.features.map((f) => (f.iconUrl ? { label: f.label, iconUrl: f.iconUrl } : { label: f.label }));
  }
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// The hint (?o=<handle>): this browser session only
// ---------------------------------------------------------------------------

export const HINT_KEY = 'portal_org_hint_v1';

export function validHandle(h: string | null | undefined): string | null {
  return typeof h === 'string' && PUBLIC_HANDLE_RE.test(h) ? h : null;
}

/** Remember the `?o=` of this URL for the session (sessionStorage only). Returns the hint in force. */
export function captureHint(
  search: string,
  store: Pick<Storage, 'getItem' | 'setItem'> | null = session(),
): string | null {
  const fromUrl = validHandle(new URLSearchParams(search).get('o'));
  try {
    if (fromUrl) store?.setItem(HINT_KEY, fromUrl);
    return fromUrl ?? validHandle(store?.getItem(HINT_KEY));
  } catch {
    return fromUrl; // storage blocked: the URL's hint still applies to this page
  }
}

export function setHint(handle: string | null, store: Pick<Storage, 'setItem' | 'removeItem'> | null = session()) {
  try {
    if (handle) store?.setItem(HINT_KEY, handle);
    else store?.removeItem(HINT_KEY);
  } catch {
    /* storage blocked */
  }
}

function session(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Links that keep the hint
// ---------------------------------------------------------------------------

/** An internal path only (e.g. "/lease"): never an external or protocol-relative URL. */
export function safeNext(raw: string | null): string {
  return raw && /^\/[A-Za-z0-9/_-]*$/.test(raw) && !raw.startsWith('//') ? raw : '/';
}

/** Where a signed-out visit to a protected page goes: sign-in, keeping where it was going and the org hint. */
export function loginPathFor(pathname: string, hint: string | null, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams(extra);
  if (pathname !== '/' && safeNext(pathname) === pathname) p.set('next', pathname);
  if (validHandle(hint)) p.set('o', hint!);
  const q = p.toString();
  return q ? `/login?${q}` : '/login';
}

/** Where sign-in leads: the page asked for, with the org hint kept. */
export function afterLoginPath(next: string, hint: string | null): string {
  const h = validHandle(hint);
  if (!h) return next;
  return `${next}${next.includes('?') ? '&' : '?'}o=${h}`;
}
